export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  image: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  content: ArticleSection[];
  relatedArticles: string[];
}

export interface ArticleSection {
  type: 'heading' | 'paragraph' | 'image' | 'quote' | 'list';
  content: string;
  items?: string[];
}

export const articles: Article[] = [
  {
    slug: 'japan-sakura-guide',
    title: '日本樱花季完全攻略：从南到北追樱之旅',
    excerpt: '每年春天，日本列岛从南到北被樱花染成粉色。本文将带你领略从冲绳到北海道的追樱路线，感受最极致的日本春色。',
    category: '东亚春色',
    categorySlug: 'east-asia',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20cherry%20blossom%20tunnel%20in%20full%20bloom%20at%20Kyoto%20philosophers%20path%2C%20canal%20lined%20with%20weeping%20cherry%20trees%2C%20soft%20pink%20petals%20reflecting%20on%20water%2C%20traditional%20kimono%20figures%20walking%20in%20distance%2C%20golden%20hour%20warm%20light%2C%20dreamy%20romantic%20atmosphere%2C%20editorial%20travel%20photography&width=1200&height=600&seq=japan-sakura-article&orientation=landscape',
    author: '林晓春',
    date: '2026-02-15',
    readTime: '12分钟',
    tags: ['日本', '樱花', '春季旅行', '赏花攻略', '京都', '东京'],
    content: [
      { type: 'heading', content: '日本樱花季：一年一度的春日盛典' },
      { type: 'paragraph', content: '每年三月至五月，日本全国从南至北依次迎来樱花盛开。这场被称为"樱花前线"的自然奇观，吸引了全球数百万游客前来朝圣。从冲绳的寒绯樱，到北海道的虾夷山樱，每一处都有其独特的魅力。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Mount%20Fuji%20with%20cherry%20blossoms%20in%20foreground%20at%20Lake%20Kawaguchiko%2C%20iconic%20Japanese%20landscape%2C%20pink%20sakura%20trees%20framing%20snow-capped%20mountain%2C%20calm%20lake%20reflection%2C%20clear%20spring%20sky%2C%20picturesque%20travel%20scene%2C%20editorial%20photography&width=1200&height=600&seq=japan-sakura-detail-01&orientation=landscape' },
      { type: 'heading', content: '最佳赏樱地点推荐' },
      { type: 'paragraph', content: '日本各地都有令人叹为观止的赏樱名所。以下是不可错过的经典之地：' },
      {
        type: 'list',
        content: '',
        items: [
          '东京上野恩赐公园——城市中的樱花隧道，约800株染井吉野樱同时绽放',
          '京都哲学之道——沿着运河漫步，两岸垂樱如瀑布般倾泻',
          '大阪城公园——古城与樱花的完美结合，夜樱尤为迷人',
          '奈良吉野山——约3万株樱花覆盖整座山，被誉为日本第一赏樱胜地',
          '北海道函馆五棱郭——星形城堡被粉色樱花环绕，空中俯瞰绝美',
        ],
      },
      { type: 'heading', content: '追樱时间表' },
      { type: 'paragraph', content: '樱花花期极短，从开花到满开通常只有一周左右。合理规划时间至关重要：冲绳地区最早，约一月下旬至二月上旬；九州和四国地区约三月中下旬；东京、大阪、京都等关东关西核心地区约三月下旬至四月上旬；东北地区约四月中下旬；北海道最晚，约五月上旬。建议提前关注日本气象厅发布的樱花前线预报，灵活调整行程。' },
      { type: 'quote', content: '"樱花之美，不在于永恒，而在于那短暂而灿烂的绽放。正如人生，正因为有限，才显得格外珍贵。"' },
      { type: 'heading', content: '赏樱礼仪与实用建议' },
      { type: 'paragraph', content: '在日本赏樱时，请遵守当地礼仪：不要攀折花枝，不要摇晃树干制造"樱吹雪"效果，野餐后将垃圾带走。建议穿着舒适的步行鞋，携带野餐垫和保暖衣物（春季早晚温差大）。热门赏樱地建议清晨前往以避开人潮，或选择夜间赏樱（夜樱）体验不同的浪漫氛围。' },
    ],
    relatedArticles: ['korea-spring-flowers', 'china-jiangnan-spring'],
  },
  {
    slug: 'korea-spring-flowers',
    title: '韩国春日花海巡礼：从济州油菜花到首尔樱花',
    excerpt: '韩国的春天是一场缤纷的花之盛宴。济州岛的油菜花田、首尔的樱花大道、庆州的古都花景，每一处都让人流连忘返。',
    category: '东亚春色',
    categorySlug: 'east-asia',
    image: 'https://readdy.ai/api/search-image?query=Jeju%20Island%20canola%20flower%20fields%20in%20full%20bloom%20during%20spring%2C%20endless%20yellow%20flower%20carpet%20with%20traditional%20Korean%20stone%20wall%20and%20wind%20turbines%20in%20distance%2C%20blue%20sky%20with%20fluffy%20clouds%2C%20bright%20cheerful%20spring%20scene%2C%20travel%20editorial%20photography&width=1200&height=600&seq=korea-spring-article&orientation=landscape',
    author: '朴慧媛',
    date: '2026-02-20',
    readTime: '10分钟',
    tags: ['韩国', '济州岛', '油菜花', '赏花', '首尔', '樱花'],
    content: [
      { type: 'heading', content: '韩国春天的多样色彩' },
      { type: 'paragraph', content: '韩国的春天层次丰富，从南端济州岛的油菜花开始，一路向北延伸到首尔的樱花大道。济州岛的春天最为独特，火山地貌与金色油菜花形成强烈的视觉冲击。而在首尔，古老的宫殿与现代都市在樱花的映衬下展现出独特的风情。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Gyeongbokgung%20Palace%20in%20Seoul%20with%20cherry%20blossoms%2C%20traditional%20Korean%20architecture%20with%20curved%20roofs%2C%20pink%20sakura%20trees%20in%20full%20bloom%2C%20people%20in%20hanbok%20traditional%20clothing%2C%20spring%20sunshine%2C%20harmonious%20cultural%20scene%2C%20editorial%20photography&width=1200&height=600&seq=korea-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '济州岛：油菜花与火山岛的春日对话' },
      { type: 'paragraph', content: '济州岛的春天从三月开始。成片的油菜花在火山石墙之间铺展开来，金色的花海与蓝色的海洋、黑色的火山岩形成绝美的色彩对比。推荐前往城山日出峰脚下的油菜花田，在这里可以同时欣赏到花海、火山和海景的三重美景。涉地可支的海岸步道也是绝佳的赏花路线。' },
      { type: 'heading', content: '首尔：古宫与樱花的时空交错' },
      { type: 'paragraph', content: '四月是首尔最美的时节。景福宫、昌德宫等古宫殿被盛开的樱花环绕，穿着韩服的游客穿梭其间，仿佛时光倒流。南山塔下的樱花步道、汝矣岛轮中路的樱花隧道、石村湖水的环湖樱景，都是首尔市内不可错过的赏樱名所。' },
      { type: 'quote', content: '"济州的春天是大地的诗篇，每一朵油菜花都是写在大地上的金色音符。"' },
      { type: 'heading', content: '庆州：千年古都的春日雅韵' },
      { type: 'paragraph', content: '庆州作为新罗千年古都，春天别有一番韵味。大陵苑的古坟群被春花点缀，雁鸭池的倒影中映出花与古建的交融。这里的花期稍晚于首尔，适合四月中下旬前往，可以避开首尔的赏樱人潮，享受更加宁静的春日时光。' },
    ],
    relatedArticles: ['japan-sakura-guide', 'china-jiangnan-spring'],
  },
  {
    slug: 'china-jiangnan-spring',
    title: '江南春日古镇巡礼：水墨画中的春天',
    excerpt: '烟花三月下江南。苏州园林、杭州西湖、乌镇水乡，当春天降临江南，白墙黛瓦间弥漫着桃红柳绿的温柔诗意。',
    category: '东亚春色',
    categorySlug: 'east-asia',
    image: 'https://readdy.ai/api/search-image?query=Chinese%20Jiangnan%20water%20town%20in%20spring%2C%20white%20walls%20and%20dark%20tile%20roofs%2C%20peach%20blossoms%20along%20ancient%20canal%2C%20stone%20arch%20bridge%20reflection%20in%20calm%20water%2C%20weeping%20willow%20trees%2C%20misty%20morning%20atmosphere%2C%20traditional%20Chinese%20ink%20painting%20aesthetic%2C%20editorial%20travel%20photography&width=1200&height=600&seq=china-spring-article&orientation=landscape',
    author: '苏婉清',
    date: '2026-03-01',
    readTime: '11分钟',
    tags: ['中国', '江南', '古镇', '苏州', '杭州', '春天'],
    content: [
      { type: 'heading', content: '烟花三月下江南' },
      { type: 'paragraph', content: '"故人西辞黄鹤楼，烟花三月下扬州。"李白笔下的江南春天，千百年来始终是中国文人心中最美的意象。当春风拂过长江以南，桃花、杏花、梨花次第开放，古镇水乡在朦胧烟雨中苏醒，一派温柔婉约的江南春色徐徐展开。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=West%20Lake%20in%20Hangzhou%20during%20spring%2C%20weeping%20willow%20trees%20by%20the%20lake%2C%20cherry%20blossoms%20and%20peach%20blossoms%20in%20full%20bloom%2C%20traditional%20Chinese%20pagoda%20on%20distant%20hill%2C%20wooden%20boats%20on%20calm%20water%2C%20soft%20morning%20mist%2C%20ink%20painting%20style%20atmosphere&width=1200&height=600&seq=china-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '苏州园林：一步一景的春日画卷' },
      { type: 'paragraph', content: '苏州园林在春天展现出最美的姿态。拙政园的紫藤花架下，一串串紫色花穗随风摇曳；留园的石笋与桃花相映成趣；网师园的月到风来亭边，玉兰盛开如雪。苏州园林讲究"移步换景"，春日里的每一扇花窗、每一道月洞门外，都是一幅活的春色画卷。' },
      { type: 'heading', content: '杭州西湖：人间天堂的春日诗意' },
      { type: 'paragraph', content: '西湖的春天，是苏堤春晓的桃红柳绿，是太子湾公园的郁金香花海，是龙井茶园的新芽吐翠。建议租一辆自行车环湖骑行，或乘坐手划船泛舟湖上，从不同角度感受西湖春色的层次之美。清晨时分的西湖最是动人，薄雾轻笼，湖山如黛。' },
      { type: 'quote', content: '"江南无所有，聊赠一枝春。"——陆凯《赠范晔诗》' },
      { type: 'heading', content: '乌镇西塘：水乡古镇的春日慢生活' },
      { type: 'paragraph', content: '乌镇和西塘在春天有着独特的韵味。晨雾中，白墙黛瓦的民居倒映在河面上，河边的桃花探出墙头，乌篷船缓缓划过水面。建议在水乡住上一晚，清晨在鸟鸣中醒来，推开临河的窗，看晨光一点点洒在水面上，这才是江南春天最地道的打开方式。' },
    ],
    relatedArticles: ['japan-sakura-guide', 'vietnam-hoi-an-spring'],
  },
  {
    slug: 'vietnam-hoi-an-spring',
    title: '越南会安古城春日漫步：灯笼与花海的浪漫邂逅',
    excerpt: '春天是会安最美的季节。古城灯笼摇曳，秋盆河畔花团锦簇，这座世界文化遗产小城在春日里散发出迷人的东南亚风情。',
    category: '东南亚春色',
    categorySlug: 'southeast-asia',
    image: 'https://readdy.ai/api/search-image?query=Hoi%20An%20ancient%20town%20in%20Vietnam%20during%20spring%2C%20colorful%20traditional%20silk%20lanterns%20hanging%20along%20yellow%20colonial%20buildings%2C%20bougainvillea%20flowers%20blooming%20on%20ancient%20walls%2C%20Thu%20Bon%20River%20with%20wooden%20boats%2C%20warm%20golden%20evening%20light%2C%20romantic%20Southeast%20Asian%20atmosphere&width=1200&height=600&seq=vietnam-spring-article&orientation=landscape',
    author: '阮明芳',
    date: '2026-03-05',
    readTime: '9分钟',
    tags: ['越南', '会安', '古城', '灯笼', '东南亚', '春季'],
    content: [
      { type: 'heading', content: '会安的春天：东南亚最浪漫的古城' },
      { type: 'paragraph', content: '会安，这座位于越南中部的世界文化遗产古城，在春天展现出无可比拟的魅力。三月的会安气温宜人，阳光温暖而不灼热，古城里的九重葛和凤凰花竞相绽放，将黄色的老建筑装点得生机勃勃。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Vietnamese%20women%20in%20traditional%20ao%20dai%20dress%20walking%20through%20Hoi%20An%20ancient%20town%2C%20colorful%20lanterns%20overhead%2C%20yellow%20colonial%20walls%20with%20climbing%20bougainvillea%2C%20cobblestone%20street%2C%20spring%20sunlight%2C%20cultural%20charm%2C%20editorial%20travel%20photography&width=1200&height=600&seq=vietnam-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '灯笼之夜：会安的标志性体验' },
      { type: 'paragraph', content: '每逢农历十四，会安全城熄灭电灯，数千盏丝绸灯笼同时点亮，将古城变成一片温暖的灯海。春天的新月之夜，秋盆河上漂流着祈福的莲花灯，河面倒映着两岸的灯笼光，整个古城仿佛漂浮在星河之中。这是会安春天最不可错过的浪漫时刻。' },
      { type: 'heading', content: '春日美食之旅' },
      { type: 'paragraph', content: '会安的春天也是美食的季节。街头巷尾飘散着高楼面的香气、白玫瑰饺子的清甜。推荐在清晨逛一逛会安中心市场，新鲜的春菜和热带水果琳琅满目。一定要尝尝会安特色的滴漏咖啡，坐在河边的老咖啡馆里，看春日的阳光洒在古城的黄墙上。' },
      { type: 'quote', content: '"会安的春天是诗与远方的交汇处，是历史与当下最温柔的重逢。"' },
    ],
    relatedArticles: ['thailand-chiang-mai-flowers', 'china-jiangnan-spring'],
  },
  {
    slug: 'thailand-chiang-mai-flowers',
    title: '泰国清迈热带花节：玫瑰之城春日漫游指南',
    excerpt: '二月的清迈，热带花卉竞相绽放。从古城寺庙到素贴山，从花卉节到夜市美食，这座"泰北玫瑰"在春天散发出最迷人的芬芳。',
    category: '东南亚春色',
    categorySlug: 'southeast-asia',
    image: 'https://readdy.ai/api/search-image?query=Chiang%20Mai%20flower%20festival%20in%20Thailand%2C%20colorful%20tropical%20flowers%20on%20parade%20floats%2C%20traditional%20Thai%20temples%20with%20golden%20spires%2C%20orchids%20and%20frangipani%20flowers%2C%20mountain%20backdrop%2C%20festive%20spring%20atmosphere%2C%20vibrant%20Southeast%20Asian%20scene&width=1200&height=600&seq=thailand-spring-article&orientation=landscape',
    author: '阿披差',
    date: '2026-03-10',
    readTime: '10分钟',
    tags: ['泰国', '清迈', '花节', '热带花卉', '寺庙', '东南亚'],
    content: [
      { type: 'heading', content: '清迈：泰北玫瑰的春日绽放' },
      { type: 'paragraph', content: '清迈被誉为"泰北玫瑰"，每年二月举办的花卉节是这座城市最盛大的春日庆典。届时，城市公园变身为花的海洋，数以万计的热带花卉组成精美的园艺作品，花车游行穿城而过，整个清迈沉浸在花与色彩的狂欢之中。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Doi%20Suthep%20temple%20in%20Chiang%20Mai%20surrounded%20by%20tropical%20flowers%2C%20golden%20stupa%20with%20intricate%20details%2C%20staircase%20with%20naga%20serpent%20railings%2C%20blooming%20frangipani%20trees%2C%20mountain%20view%2C%20clear%20blue%20sky%2C%20spiritual%20atmosphere&width=1200&height=600&seq=thailand-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '清迈花卉节亮点' },
      { type: 'paragraph', content: '花卉节通常在二月的第一个周末举行，持续三天。主要活动集中在Buak Hat城市公园。巨大的花车由数十万朵鲜花装饰而成，每辆花车都有不同的主题。除了花车游行，还有花卉展览、园艺比赛、传统舞蹈表演和美食摊位。清晨的花市尤其值得一逛，可以买到多种本地特有的热带兰花。' },
      { type: 'heading', content: '素贴山的春日朝圣' },
      { type: 'paragraph', content: '春天的素贴山空气清新，双龙寺的金塔在阳光下熠熠生辉。沿306级台阶攀登而上，两旁是盛开的九重葛和鸡蛋花。山顶可以俯瞰整个清迈城，春天能见度高时，甚至可以看到远处的茵他侬山。建议清晨前往，避开正午的炎热。' },
      { type: 'quote', content: '"清迈的春天不是季节，而是一种心境。在这里，时光慢下来，心灵花盛开。"' },
    ],
    relatedArticles: ['vietnam-hoi-an-spring', 'indonesia-bali-spring'],
  },
  {
    slug: 'indonesia-bali-spring',
    title: '印尼巴厘岛春日梯田：众神之岛的绿色诗篇',
    excerpt: '春季的巴厘岛，乌布梯田绿意盎然，寺庙祭祀香气缭绕，印度洋的海风送来春天的问候。在这座众神之岛上，春天以独特的方式铺展开来。',
    category: '东南亚春色',
    categorySlug: 'southeast-asia',
    image: 'https://readdy.ai/api/search-image?query=Bali%20Ubud%20rice%20terraces%20in%20spring%20with%20vibrant%20green%20paddies%2C%20coconut%20palm%20trees%2C%20traditional%20Balinese%20temples%20with%20intricate%20stone%20carvings%2C%20farmers%20working%20in%20fields%2C%20morning%20mist%20over%20valley%2C%20tropical%20paradise%20atmosphere&width=1200&height=600&seq=bali-spring-article&orientation=landscape',
    author: '韦安卡',
    date: '2026-03-15',
    readTime: '9分钟',
    tags: ['印尼', '巴厘岛', '梯田', '乌布', '热带', '春季'],
    content: [
      { type: 'heading', content: '巴厘岛的春天：众神之岛的苏醒' },
      { type: 'paragraph', content: '巴厘岛没有传统意义上的四季，但三月到五月的春季是岛上最舒适的季节。雨季渐远，天空湛蓝，乌布的梯田在充沛的雨水滋润后呈现最饱满的绿色。寺庙里的鸡蛋花和三角梅进入盛花期，整个岛屿充满生机。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Tegallalang%20rice%20terraces%20in%20Ubud%20Bali%20during%20spring%2C%20lush%20green%20terraced%20paddies%20with%20palm%20trees%2C%20traditional%20Balinese%20subak%20irrigation%20system%2C%20morning%20sunlight%20creating%20beautiful%20shadows%2C%20peaceful%20rural%20scene%2C%20travel%20editorial%20photography&width=1200&height=600&seq=bali-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '乌布梯田：巴厘岛的灵魂' },
      { type: 'paragraph', content: '德格拉朗梯田是巴厘岛最著名的春日景观。春季的梯田水量充沛，在阳光下反射出镜面般的光泽。沿着田埂漫步，你可以看到当地农民正在插秧劳作的场景。建议日出时分前往，清晨薄雾还未散去的梯田，美得如同仙境。' },
      { type: 'heading', content: '寺庙与春祭' },
      { type: 'paragraph', content: '春季也是巴厘岛众多印度教节日集中的时节。宁静日（Nyepi）是巴厘岛的新年，全岛禁火禁光禁娱乐，是世界上最独特的"沉默日"。圣泉寺（Tirta Empul）的泉水即使在最炎热的午后也保持清凉，春季前来沐浴祈福，是与巴厘岛文化最亲密的接触。' },
      { type: 'quote', content: '"在巴厘岛，春天不是用眼睛看的，而是用整个身体去感受的——是稻田泥土的气息，是寺庙线香的味道，是印度洋的风拂过皮肤的温柔。"' },
    ],
    relatedArticles: ['thailand-chiang-mai-flowers', 'sri-lanka-tea-spring'],
  },
  {
    slug: 'india-darjeeling-spring',
    title: '印度大吉岭春日茶园：喜马拉雅山麓的绿色仙境',
    excerpt: '春天的大吉岭，茶园新芽吐翠，杜鹃花开满山。乘坐蒸汽小火车穿行于云雾缭绕的茶园之间，远处是巍峨的干城章嘉峰，这里是南亚最诗意的春天。',
    category: '南亚春色',
    categorySlug: 'south-asia',
    image: 'https://readdy.ai/api/search-image?query=Darjeeling%20tea%20plantations%20in%20spring%20with%20Himalayan%20mountains%20in%20background%2C%20women%20picking%20tea%20leaves%20in%20colorful%20saris%2C%20lush%20green%20terraced%20hills%2C%20toy%20train%20on%20mountain%20railway%2C%20misty%20morning%20atmosphere%2C%20colonial%20era%20charm&width=1200&height=600&seq=india-spring-article&orientation=landscape',
    author: '拉吉夫',
    date: '2026-03-20',
    readTime: '10分钟',
    tags: ['印度', '大吉岭', '茶园', '喜马拉雅', '南亚', '春季'],
    content: [
      { type: 'heading', content: '大吉岭：喜马拉雅山麓的春之礼物' },
      { type: 'paragraph', content: '大吉岭坐落在喜马拉雅山脉南麓，海拔约2100米。每年三月至五月，这里迎来了最美的春季。茶园吐露新绿，漫山遍野的杜鹃花从粉红到深红层层晕染，清晨的云海在山谷中翻涌，远处世界第三高峰干城章嘉峰的雪顶在晨曦中闪耀金光。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Darjeeling%20Himalayan%20Railway%20toy%20train%20passing%20through%20tea%20gardens%2C%20steam%20locomotive%2C%20lush%20green%20tea%20bushes%2C%20snow-capped%20Kanchenjunga%20mountain%20in%20background%2C%20clear%20spring%20sky%2C%20nostalgic%20colonial%20atmosphere%2C%20UNESCO%20world%20heritage&width=1200&height=600&seq=india-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '大吉岭喜马拉雅铁路：蒸汽小火车的春日之旅' },
      { type: 'paragraph', content: '大吉岭喜马拉雅铁路是联合国教科文组织世界遗产，这条窄轨铁路上的蒸汽小火车（Toy Train）是体验大吉岭春天的最佳方式。火车从西里古里出发，缓缓爬升，穿行于茶园、森林和山间小镇之间。春天沿途杜鹃花盛开，透过敞开的车窗，花香与山风一同涌入，这是世界上最美的火车旅程之一。' },
      { type: 'heading', content: '茶园体验：从一片叶子到一杯春茶' },
      { type: 'paragraph', content: '春季是大吉岭茶最珍贵的"初摘"（First Flush）季节。此时的茶叶经过冬季的休养，蕴含最丰富的香气和营养成分。推荐参观Happy Valley茶园，可以体验从采茶到制茶的全过程，最后在茶园品茶室里，面对着喜马拉雅雪山，品尝一杯刚刚制成的春茶。' },
      { type: 'quote', content: '"大吉岭的春天，是喜马拉雅送给世界的一杯清茶——纯净、澄澈、回味悠长。"' },
    ],
    relatedArticles: ['nepal-pokhara-rhododendron', 'sri-lanka-tea-spring'],
  },
  {
    slug: 'nepal-pokhara-rhododendron',
    title: '尼泊尔博卡拉杜鹃花季：雪山与花海的交响曲',
    excerpt: '春天的博卡拉，鱼尾峰下杜鹃怒放。徒步安纳普尔纳山区的春日花径，雪山、湖泊、花海构成一幅令人窒息的南亚春色画卷。',
    category: '南亚春色',
    categorySlug: 'south-asia',
    image: 'https://readdy.ai/api/search-image?query=Phewa%20Lake%20in%20Pokhara%20Nepal%20with%20Machhapuchhre%20Fishtail%20mountain%20reflection%2C%20rhododendron%20trees%20in%20full%20red%20bloom%20on%20hillsides%2C%20colorful%20wooden%20boats%20on%20calm%20lake%2C%20spring%20morning%20with%20clear%20sky%2C%20Himalayan%20paradise%20scene&width=1200&height=600&seq=nepal-spring-article&orientation=landscape',
    author: '明玛',
    date: '2026-03-25',
    readTime: '11分钟',
    tags: ['尼泊尔', '博卡拉', '杜鹃花', '雪山', '喜马拉雅', '徒步'],
    content: [
      { type: 'heading', content: '博卡拉：喜马拉雅山下的春色天堂' },
      { type: 'paragraph', content: '博卡拉位于尼泊尔中部，坐落在安纳普尔纳山脉脚下，费瓦湖畔。每年春季三月至五月，这座宁静的小城被杜鹃花装点成红色与粉色交织的花之国度。巍峨的鱼尾峰倒映在平静的费瓦湖面上，湖畔山坡上野生杜鹃如火如荼地绽放，这是喜马拉雅地区最壮美的春日画卷。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Annapurna%20trekking%20trail%20in%20spring%20with%20rhododendron%20forests%20in%20full%20bloom%2C%20red%20and%20pink%20rhododendron%20trees%20creating%20natural%20tunnel%2C%20snow-capped%20Himalayan%20peaks%20visible%20through%20flowers%2C%20clear%20blue%20sky%2C%20adventure%20travel&width=1200&height=600&seq=nepal-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '安纳普尔纳春季徒步：走入杜鹃花的天堂' },
      { type: 'paragraph', content: '春季是安纳普尔纳地区徒步的最佳季节。山径两旁，高大的杜鹃树形成了天然的红色隧道。从低海拔的红色杜鹃到高海拔的粉色和白色杜鹃，随着海拔的变化，花色也在不断变化。推荐Poon Hill（布恩山）三日徒步路线，难度适中，日出时可以在花海中眺望安纳普尔纳群峰的日照金山。' },
      { type: 'heading', content: '费瓦湖：雪山倒影与春日晨雾' },
      { type: 'paragraph', content: '费瓦湖的春晨是最动人的时刻。当第一缕阳光照亮鱼尾峰的雪顶，湖面上的晨雾缓缓散去，湖边寺庙的钟声在山谷中回荡。租一艘彩色的小木船，泛舟湖心，四周是雪山、花海和蓝天，时间仿佛在这一刻静止。' },
      { type: 'quote', content: '"在博卡拉，春天不是形容词，而是一个动词——它在每一朵杜鹃的绽放中行动，在每一缕晨雾的流动中呼吸。"' },
    ],
    relatedArticles: ['india-darjeeling-spring', 'sri-lanka-tea-spring'],
  },
  {
    slug: 'sri-lanka-tea-spring',
    title: '斯里兰卡春日茶山之旅：印度洋上的绿宝石',
    excerpt: '春天的斯里兰卡，中部茶山云雾缭绕，采茶女的身影点缀在翠绿的茶园中。从康提到努沃勒埃利耶，一路茶香四溢，这就是南亚最清新的春天。',
    category: '南亚春色',
    categorySlug: 'south-asia',
    image: 'https://readdy.ai/api/search-image?query=Sri%20Lanka%20Nuwara%20Eliya%20tea%20plantations%20in%20spring%2C%20rolling%20green%20hills%20covered%20with%20tea%20bushes%2C%20women%20in%20colorful%20saris%20picking%20tea%20leaves%2C%20colonial%20bungalow%20on%20hilltop%2C%20misty%20morning%20atmosphere%2C%20Indian%20Ocean%20in%20far%20distance&width=1200&height=600&seq=sri-lanka-spring-article&orientation=landscape',
    author: '阿马拉',
    date: '2026-04-01',
    readTime: '10分钟',
    tags: ['斯里兰卡', '茶山', '努沃勒埃利耶', '印度洋', '南亚', '春季'],
    content: [
      { type: 'heading', content: '斯里兰卡：印度洋上的春日翡翠' },
      { type: 'paragraph', content: '斯里兰卡古称锡兰，是享誉世界的红茶产地。每年春天三月至五月，中部山区的茶园进入最佳观赏期。层层叠叠的茶山如绿色波浪般起伏，采茶女们穿着鲜艳纱丽在茶树间穿行，英式殖民时期的庄园点缀其间。这里被称为"小英格兰"，有着意想不到的南亚春日风情。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Nuwara%20Eliya%20hill%20country%20Sri%20Lanka%20spring%20scenery%2C%20endless%20green%20tea%20plantations%20with%20winding%20roads%2C%20colonial%20era%20post%20office%20building%2C%20horse%20track%2C%20cool%20misty%20climate%2C%20flowers%20blooming%20in%20English%20style%20gardens%2C%20unique%20tropical%20highland%20charm&width=1200&height=600&seq=sri-lanka-spring-detail-01&orientation=landscape' },
      { type: 'heading', content: '茶园火车：世界最美的火车线路之一' },
      { type: 'paragraph', content: '从康提到埃拉的茶园火车是斯里兰卡春天不可错过的体验。蓝色的小火车缓缓穿行于翠绿的茶山之间，窗外是绵延不绝的茶园和远处的山谷。建议购买二等或三等车厢票，打开窗户，让山风和茶香扑面而来。当火车经过著名的九孔桥时，周围的茶山和瀑布构成一幅绝美的画面。' },
      { type: 'heading', content: '努沃勒埃利耶：南亚的小英格兰' },
      { type: 'paragraph', content: '努沃勒埃利耶海拔约1900米，春季的清晨和傍晚有着难得的凉意。市区保留了大量英殖民时期的建筑：粉红邮局、高尔夫球场、赛马场和维多利亚风格的酒店。春天这里的气候与英国乡村惊人地相似，所以被称为"小英格兰"。附近的格雷戈里湖是本地人春日郊游的热门去处。' },
      { type: 'quote', content: '"斯里兰卡的春天是一杯锡兰红茶——初入口是高原的清冽，回味是印度洋的温暖。"' },
    ],
    relatedArticles: ['india-darjeeling-spring', 'nepal-pokhara-rhododendron'],
  },
];